import { useTranslation } from "react-i18next";
// @ts-ignore
import { MyUsageTooltip } from './MyUsageTooltip';
// @ts-ignore
import { useResponsive } from '../../lib/useResponsive';
// @ts-ignore
import { UcTouch } from 'components/common/UcTouch';
import { Brush, CartesianGrid,  Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useTheme } from "@mui/material";
import { ChartInstanceOfMuiTheme, UomAxisLabel } from "../readings/CustomerSummaryChartUtils";
import { useEffect, useMemo, useState, useCallback } from "react";
import { UsageHelpers } from "./MyUsage.helpers";

interface UsageGenerationChartProps {
    readonly dataPoints: any;
    readonly uomDisplay: any;
    readonly uom: any;
    readonly groupBy: string;
}

function MyUsageGenerationChart({ dataPoints, uom, uomDisplay, groupBy }: UsageGenerationChartProps) {
    const theme = useTheme<ChartInstanceOfMuiTheme>();
    const { t } = useTranslation();
    const { isMobile } = useResponsive();

    const totalColumns = dataPoints?.length;
    const [columnsToShow, setColumnsToShow] = useState<number>(13);
    const [startChartScroll, setStartChartScroll] = useState<number>(totalColumns - columnsToShow);
    const [endChartScroll, setEndChartScroll] = useState<number>(totalColumns - 1);
    const [activeTooltipIndex, setActiveTooltipIndex] = useState<number | null>(null);

    useEffect(() => {
        const { columnsToShow, startChartScroll } = UsageHelpers.calculateColumnsToShow(groupBy, isMobile, totalColumns);
        setColumnsToShow(columnsToShow);
        setStartChartScroll(startChartScroll);
        setEndChartScroll(totalColumns - 1);
    }, [totalColumns, groupBy, isMobile]);

    const uomAxisLabel: { [key: string]: UomAxisLabel } = {
        'KWH': t("Electricity ({{uom}})", { uom: uomDisplay })
    }

    const graphFontColor = theme?.props?.Rechart?.axisColor;
    const graphTextStyle = {
        fill: graphFontColor,
        fontSize: isMobile ? 12 : 16
    };

    const { totalUsageDomain } = useMemo(() => UsageHelpers.calculateAxisDomains(dataPoints),
        [dataPoints]
    );

    const handleTouch = useCallback((event: React.MouseEvent<SVGCircleElement>, dataIndex: number) => {
        event.stopPropagation();
        setActiveTooltipIndex(dataIndex);
    }, []);

    const MobileActiveDot = useMemo(() => {
        return (props: any) => {
            const { cx, cy, dataIndex } = props;
            return (
                <circle
                    cx={cx}
                    cy={cy}
                    r={30}
                    fill="transparent"
                    stroke="#8884d8"
                    strokeWidth={2}
                    onClick={(e) => handleTouch(e, dataIndex)}
                />
            );
        };
    }, [handleTouch]);

    return (
        <div className="myUsageGenerationChart" data-cy-id={"myUsageGenerationChart"}>
            <UcTouch
                style={{ width: "100%" }}
                columnsToShow={columnsToShow}
                totalColumns={totalColumns}
                startX={startChartScroll}
                onRangeChangeX={(newStart: any, newEnd: any) => {
                    setStartChartScroll(Math.max(newStart, 0));
                    setEndChartScroll(newEnd);
                }}
            >
                <ResponsiveContainer width={"100%"} height={"100%"} maxHeight={630} minHeight={isMobile ? "450px" : "600px"} className="recharts-container-scrollable">
                    <LineChart
                        data={dataPoints}
                        margin={{
                            top: 15,
                            right: (isMobile ? 10 : 20),
                            bottom: 20,
                            left: (isMobile ? 0 : 10),
                        }}
                        // @ts-ignore
                        fillOpacity="0.7"
                        onClick={() => isMobile && setActiveTooltipIndex(null)}
                    >
                        <Line
                            type="monotone"
                            dataKey="totalUsage"
                            stroke="#8884d8"
                            activeDot={isMobile ? <MobileActiveDot /> : { r: 8 }}
                            isAnimationActive={false}
                        />
                        <CartesianGrid strokeDasharray="5 5" />
                        <XAxis
                            dataKey="label"
                            scale="auto"
                            interval={isMobile ? 1 : 0} // always display ticks
                            angle={-45}
                            textAnchor="end"
                            tick={{ fill: graphFontColor, fontSize: 12 }}
                        />
                        <YAxis
                            domain={[totalUsageDomain.min, totalUsageDomain.max]}
                            label={{
                                value: uomAxisLabel[uom],
                                angle: -90,
                                position: "left",
                                style: graphTextStyle,
                                offset: isMobile ? -10 : 0
                            }}
                            tick={graphTextStyle}
                            axisLine={!isMobile}
                        />
                        <Tooltip
                            content={
                                <MyUsageTooltip
                                    uom={uomDisplay}
                                    showCost={false}
                                    hideRateSummary={true}
                                />
                            }
                            active={isMobile ? activeTooltipIndex !== null : undefined}
                        />
                        <Legend formatter={() => t('Generation')} />
                        <Brush
                            updateId={startChartScroll}
                            startIndex={startChartScroll}
                            endIndex={endChartScroll}
                            dataKey="label"
                        />
                    </LineChart>
                </ResponsiveContainer>
            </UcTouch>
        </div>  
    );
}

export default MyUsageGenerationChart;